<template>
  <div class="max-w-4xl space-y-4">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-gray-700">Populations</span>
      <button @click="newPopulation" class="btn-primary-sm">+ New</button>
    </div>
    <p class="text-xs text-gray-500">
      A population is the SQL that defines a frame's rows. The one requirement:
      it must emit <code>BENE_MBI_ID</code> — every attribute joins on it.
      Every column it emits flows into frame.csv.
    </p>

    <div class="divide-y divide-gray-100 border border-gray-200 rounded">
      <div
        v-for="pop in populations"
        :key="pop.id"
        @click="editPopulation(pop)"
        :class="[
          'flex items-center justify-between px-4 py-2 cursor-pointer text-sm',
          editing?.id === pop.id ? 'bg-blue-50' : 'hover:bg-gray-50'
        ]"
      >
        <span class="font-medium text-gray-900">{{ pop.name }}</span>
        <span class="text-xs text-gray-500 truncate ml-4">{{ pop.description }}</span>
      </div>
      <div v-if="!populations.length" class="px-4 py-6 text-center text-sm text-gray-400">No populations yet</div>
    </div>

    <div v-if="editing" class="border border-gray-200 rounded p-4 space-y-3">
      <div class="flex gap-3">
        <input
          v-model="editing.name"
          placeholder="Population name..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium"
        />
        <button @click="testSql" :disabled="testing" class="btn-primary-sm">
          {{ testing ? 'Testing...' : 'Test SQL' }}
        </button>
        <button @click="save" :disabled="saving || !editing.name" class="btn-primary-sm">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
      <input
        v-model="editing.description"
        placeholder="Description..."
        class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
      />
      <textarea
        v-model="editing.sql_query"
        rows="14"
        spellcheck="false"
        class="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono leading-relaxed"
        placeholder="SELECT BENE_MBI_ID, ... FROM ... WHERE TENANT_ID = {tenant_id}"
      ></textarea>
      <div v-if="testResult" :class="['text-xs px-3 py-2 rounded', testResult.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700']">
        <template v-if="testResult.valid">Valid. Columns: {{ testResult.columns.join(', ') }}</template>
        <template v-else>{{ testResult.error }}</template>
      </div>
      <p v-if="saveError" class="text-xs text-red-600">{{ saveError }}</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PopulationManager',
  data() {
    return {
      populations: [],
      editing: null,
      testing: false,
      saving: false,
      testResult: null,
      saveError: null,
    };
  },
  methods: {
    async fetchPopulations() {
      try {
        const response = await axios.get('api/impact_analytics/populations/');
        this.populations = response.data;
      } catch (error) {
        console.error('Error fetching populations:', error);
      }
    },
    newPopulation() {
      this.editing = { id: null, name: '', description: '', sql_query: '' };
      this.testResult = null;
      this.saveError = null;
    },
    editPopulation(pop) {
      this.editing = {
        id: pop.id,
        name: pop.name,
        description: pop.description || '',
        sql_query: pop.sql_query || '',
      };
      this.testResult = null;
      this.saveError = null;
      if (!this.editing.sql_query) this.loadDetail(pop.id);
    },
    async loadDetail(id) {
      try {
        const response = await axios.get(`api/impact_analytics/populations/${id}/`);
        if (this.editing?.id === id) this.editing.sql_query = response.data.sql_query || '';
      } catch (error) {
        console.error('Error loading population:', error);
      }
    },
    async testSql() {
      this.testing = true;
      this.testResult = null;
      try {
        const response = await axios.post('api/impact_analytics/populations/test-sql/', {
          sql_query: this.editing.sql_query,
        });
        this.testResult = response.data;
      } catch (error) {
        this.testResult = { valid: false, error: error.response?.data?.error || 'Test failed' };
      } finally {
        this.testing = false;
      }
    },
    async save() {
      this.saving = true;
      this.saveError = null;
      const payload = {
        name: this.editing.name,
        description: this.editing.description,
        sql_query: this.editing.sql_query,
        attribute_type: 'categorical',
      };
      try {
        if (this.editing.id) {
          await axios.put(`api/impact_analytics/populations/${this.editing.id}/`, payload);
        } else {
          const response = await axios.post('api/impact_analytics/populations/', payload);
          this.editing.id = response.data.id;
        }
        await this.fetchPopulations();
      } catch (error) {
        const detail = error.response?.data;
        this.saveError = typeof detail === 'object'
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ').slice(0, 200)
          : 'Save failed';
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.fetchPopulations();
  },
};
</script>
